import React, { useState } from 'react';
import {
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    Menu,
    MenuItem,
    FormControlLabel,
    Switch,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useEventType, useEditEventType, useDeleteEventType } from '../../hooks/useEventType';
import { usePermission } from '../../hooks/usePermission';
import DataTable from '../shared/DataTable';
import { EditEventTypesPermission, DeleteEventTypesPermission } from '../../constants/permissions';

const EventTypeTable = () => {
    const { data: eventTypes, isLoading } = useEventType();
    const editEventType = useEditEventType();
    const deleteEventType = useDeleteEventType();
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedEventType, setSelectedEventType] = useState(null);
    const [editFormData, setEditFormData] = useState({
        name: '',
        description: '',
        allow_app_sign: false,
        can_add_attendees: false,
        can_delete_attendees: false,
        can_edit_attendees: false,
        needs_organization: false,
    });
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedActionId, setSelectedActionId] = useState(null);

    const { data: canEditEventType } = usePermission({
        permissionName: EditEventTypesPermission
    });

    const { data: canDeleteEventType } = usePermission({
        permissionName: DeleteEventTypesPermission
    });

    const handleMenuClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setSelectedActionId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedActionId(null);
    };

    const handleEditClick = (eventType) => {
        setSelectedEventType(eventType);
        setEditFormData({
            name: eventType.name,
            description: eventType.description || '',
            allow_app_sign: eventType.allow_app_sign || false,
            can_add_attendees: eventType.can_add_attendees || false,
            can_delete_attendees: eventType.can_delete_attendees || false,
            can_edit_attendees: eventType.can_edit_attendees || false,
            needs_organization: eventType.needs_organization || false,
        });
        setEditDialogOpen(true);
        handleMenuClose();
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
        setSelectedEventType(null);
    };

    const handleEditSubmit = async () => {
        try {
            await editEventType.mutateAsync({
                id: selectedEventType.id,
                data: editFormData,
            });
            handleEditClose();
        } catch (error) {
            console.error('Error editing event type:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this event type?')) {
            try {
                await deleteEventType.mutateAsync(id);
                handleMenuClose();
            } catch (error) {
                console.error('Error deleting event type:', error);
            }
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
        },
        {
            name: 'Description',
            selector: row => row.description || '',
            sortable: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <IconButton onClick={(e) => handleMenuClick(e, row.id)}>
                    <MoreVertIcon />
                </IconButton>
            ),
            width: '100px',
        },
    ];

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <DataTable
                columns={columns}
                data={eventTypes}
                searchFields={['name', 'description']}
                searchPlaceholder="Search event types..."
            />

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {canEditEventType && (
                    <MenuItem onClick={() => handleEditClick(eventTypes.find(et => et.id === selectedActionId))}>
                        <i className="fa-solid fa-edit text-green-700 mr-2 mb-1" />
                        <h6 className='text-blueColor'>Edit</h6>
                    </MenuItem>
                )}
                {canDeleteEventType && (
                    <MenuItem onClick={() => handleDelete(selectedActionId)}>
                        <i className="fa-solid fa-trash text-red-600 mr-2" />
                        <h6 className='text-blueColor'>Delete</h6>
                    </MenuItem>
                )}
            </Menu>

            <Dialog
                open={editDialogOpen}
                onClose={handleEditClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle className="bg-blueColor text-white py-4">
                    <i className="fas fa-edit text-xl mx-2"></i>
                    <span className="text-xl font-semibold">Edit Event Type</span>
                </DialogTitle>
                <DialogContent className="mt-4">
                    <div className="grid grid-cols-1 gap-4 p-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Name
                            </label>
                            <input
                                name="name"
                                value={editFormData.name}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, name: e.target.value })
                                }
                                type="text"
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                            />
                        </div>

                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                Description
                            </label>
                            <textarea
                                name="description"
                                value={editFormData.description}
                                onChange={(e) =>
                                    setEditFormData({ ...editFormData, description: e.target.value })
                                }
                                className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-purpleColor"
                                rows="4"
                            />
                        </div>

                        <div className="grid grid-cols-1 gap-4 mt-4">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editFormData.allow_app_sign}
                                        onChange={(e) =>
                                            setEditFormData({ ...editFormData, allow_app_sign: e.target.checked })
                                        }
                                    />
                                }
                                label="Allow App Sign"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editFormData.can_add_attendees}
                                        onChange={(e) =>
                                            setEditFormData({ ...editFormData, can_add_attendees: e.target.checked })
                                        }
                                    />
                                }
                                label="Can Add Attendees"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editFormData.can_delete_attendees}
                                        onChange={(e) =>
                                            setEditFormData({ ...editFormData, can_delete_attendees: e.target.checked })
                                        }
                                    />
                                }
                                label="Can Delete Attendees"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editFormData.can_edit_attendees}
                                        onChange={(e) =>
                                            setEditFormData({ ...editFormData, can_edit_attendees: e.target.checked })
                                        }
                                    />
                                }
                                label="Can Edit Attendees"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editFormData.needs_organization}
                                        onChange={(e) =>
                                            setEditFormData({ ...editFormData, needs_organization: e.target.checked })
                                        }
                                    />
                                }
                                label="Needs Organization"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6 p-4">
                        <button
                            onClick={handleEditClose}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleEditSubmit}
                            className="px-4 py-2 bg-blueColor text-white rounded"
                        >
                            Save
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default EventTypeTable; 